import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Unlink } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { providerOf } from '../../services/git/providers';
import { describeSync } from '../../services/git/gitSyncStatus';
import { sameRepo, teamRepoLabel } from '../../services/git/teamRepo';
import { isLocalScopeLink, countScopeVersions } from '../../services/git/scopeLocalGitSync';
import { useScopeProgress, progressText } from '../../services/git/scopeProgress';
import { useScopeGitHost } from './GitScopeHost';
import GitDetailHead from './GitDetailHead';
import GitMoreMenu from './GitMoreMenu';
import { LocalRow } from './GitLocalRow';
import { remoteMeta, RemoteActions, copyAddressItem } from './GitWorkspaceRow';

/** A team's owners and admins: move the team to another repository, or remove the team's (card 84628c27) — in "…". */
function teamAdminItems(scope, t, host) {
  if (scope.kind !== 'team' || !scope.canConnect || !scope.teamRepo) return [];
  return [
    { icon: <ArrowRightLeft size={14} />, label: t('gitTeamRepo.moveHint'), onClick: () => host.askMove(scope) },
    { icon: <Unlink size={14} />, label: t('gitTeamRepo.remove'), danger: true, onClick: () => host.askRemove(scope) },
  ];
}

const safeProvider = (git) => {
  try {
    return git ? providerOf({ git }) : null;
  } catch {
    return null; // a link from a newer build: shown, not actionable
  }
};

/**
 * The detail card's head for a scope (My projects, boards & documents, or a
 * team's): connected (Sync now · Pull · Open), tracked on this device
 * (Save version · Add a remote), a team repository to join, or not in a
 * repository yet (the card below offers Connect). While an operation runs,
 * its step shows under the meta line ("Reading boards · 12 of 40").
 */
export default function GitScopeHead({ scope }) {
  const { t } = useTranslation();
  const host = useScopeGitHost();
  const progress = useScopeProgress(scope.key);
  const entry = host.byWorkspace[String(scope.key)];
  const busy = host.busyKey === scope.key;
  const step = busy ? progressText(progress) : '';
  const stepNote = step ? (
    <span className="git-row-sub" role="status" data-testid="git-scope-progress">
      {step}
    </span>
  ) : null;
  const rootProps = { 'data-testid': 'git-scope-row', 'data-git-scope': scope.key };

  // The personal scope tracked on this device only (T10).
  if (isLocalScopeLink(scope.git)) {
    return (
      <LocalRow
        workspace={scope}
        entry={entry}
        syncing={busy}
        onSave={host.saveVersion}
        onAddRemote={host.openAddRemote}
        onStopTracking={host.stopTracking}
        countVersions={countScopeVersions}
        stopTitle="Stop tracking your projects, boards & documents?"
        staysText="Your projects, boards and documents stay as they are."
        testId="git-scope-row"
        rowProps={{ 'data-git-scope': scope.key, 'data-git-local': 'true' }}
        notes={stepNote}
      />
    );
  }

  // A team whose repository is stored on the team, not joined on this device yet.
  if (!scope.git && scope.kind === 'team' && scope.teamRepo) {
    return (
      <GitDetailHead
        name={scope.name}
        tone="off"
        status="Not joined"
        rootProps={{ ...rootProps, 'data-team-repo': scope.teamRepo.url }}
        meta={<span>{t('gitTeamRepo.syncsWith', { repo: teamRepoLabel(scope.teamRepo) })}</span>}
        notes={stepNote}
        actions={
          <>
            <Tooltip content={t('gitTeamRepo.joinHint')} side="bottom">
              <button type="button" className="git-btn git-btn--primary" disabled={busy} onClick={() => host.startJoin(scope)} aria-label={t('gitTeamRepo.join')}>
                {t('gitTeamRepo.join')}
              </button>
            </Tooltip>
            <GitMoreMenu disabled={busy} items={teamAdminItems(scope, t, host)} />
          </>
        }
      />
    );
  }

  if (!scope.git) {
    const locked = scope.kind === 'team' && !scope.canConnect;
    return (
      <GitDetailHead
        name={scope.name}
        tone="off"
        status="Not connected"
        // GitDetail wraps this head and its Connect card as the scope's row (git-scope-row).
        rootProps={{}}
        meta={<span>{locked ? 'Only the team’s owners and admins can connect it' : 'Projects, boards and documents — not in a repository yet'}</span>}
        notes={stepNote}
      />
    );
  }

  const provider = safeProvider(scope.git);
  const sync = describeSync(entry, { syncing: busy });
  return (
    <GitDetailHead
      name={scope.name}
      tone={sync.tone}
      status={sync.label}
      rootProps={rootProps}
      meta={remoteMeta(scope.git, entry, provider?.id, sync.label)}
      notes={
        <>
          {scope.teamRepo && scope.git.url && !sameRepo(scope.git.url, scope.teamRepo.url) && (
            <span className="git-row-error" data-testid="git-scope-other-repo">
              {t('gitTeamRepo.otherRepo', { repo: teamRepoLabel(scope.teamRepo) })}
            </span>
          )}
          {sync.tone === 'error' && entry?.lastError && entry.lastError !== sync.label && <span className="git-row-error">{entry.lastError}</span>}
          {stepNote}
        </>
      }
      actions={
        <>
          <RemoteActions
            provider={provider}
            git={scope.git}
            busy={busy}
            tone={sync.tone}
            onSync={() => host.sync(scope)}
            onPull={() => host.pull(scope)}
          />
          <GitMoreMenu disabled={busy} items={[copyAddressItem(scope.git), ...teamAdminItems(scope, t, host)]} />
        </>
      }
    />
  );
}
